import { useMemo, useState, useCallback, useEffect } from "react"
import { toast } from "react-toastify"
import {
  Col,
  Row,
  UncontrolledTooltip,
  Card,
  CardBody,
  Button,
  Badge,
} from "reactstrap"

// Custom Components
import TableContainer from "components/Common/TableContainer"
import Breadcrumbs from "components/Common/Breadcrumb"
import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"

// Custom Hooks
import {
  useVehicleImporters,
  useDeleteVehicleImporter,
} from "hooks/useVehicleImporter"

import { useListParams } from "hooks/useListParams"
import { Link, useNavigate } from "react-router-dom"
import ImporterDetailsModal from "../ImporterDetailsModal"
import ButtonTooltip from "components/Common/ButtonTooltip"
import { CanView } from "components/Common/CanView"
import { ACCESS } from "constants/accessPermissions"

const VehicleImporterList = () => {
  document.title = "Vehicle Importers List"

  // Default hooks
  const navigate = useNavigate()

  // State
  const [importer, setImporter] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)

  const { params, setPage, setSearch, resetParams, hasActiveFilters } =
    useListParams({
      page: 1,
      search: "",
    })

  const queryParams = useMemo(
    () => ({
      page: params.page,
      per_page: 10,
      ...(params.search && { search: params.search }),
    }),
    [params.page, params.search]
  )

  const {
    data: importersResponse,
    isLoading,
    refetch: refetchImporters,
    isRefetching,
  } = useVehicleImporters(queryParams)

  const importersData = importersResponse?.data?.data || []
  const paginationInfo = importersResponse?.data

  const { mutateAsync: deleteImporterMutate, isPending: deleteLoading } =
    useDeleteVehicleImporter()

  const handleAddNewClick = useCallback(() => {
    navigate("/importer/add")
  }, [])

  const handleImporterClick = useCallback(arg => {
    navigate(`/importer/${arg.slug}/edit?ref=list`)
  }, [])

  const onClickDelete = useCallback(data => {
    setImporter(data)
    setDeleteModal(true)
  }, [])

  // Importer details modal
  const [detailsModal, setDetailsModal] = useState(false)

  const toggleDetails = (data = null) => {
    setDetailsModal(prev => {
      const newState = !prev
      if (newState && data) {
        // Only set importer when opening the modal
        setImporter(data)
      }
      return newState
    })
  }

  useEffect(() => {
    if (params?.show === "true" && params?.slug) {
      toggleDetails(importersData?.find(item => item?.slug === params?.slug))
    }
  }, [params?.show, params?.slug, importersData])

  const handleDeleteImporter = useCallback(async () => {
    if (importer?.id) {
      try {
        const res = await deleteImporterMutate(importer.slug)
        toast.success(res?.message ?? "Importer deleted successfully.")
        setDeleteModal(false)
      } catch (error) {
        console.error("Failed to delete importer:", error)
        toast.error("Failed to delete importer.")
      }
    }
  }, [importer, deleteImporterMutate])

  const handleRefresh = () => refetchImporters()

  // Table columns
  const columns = useMemo(
    () => [
      { header: "ID", accessorKey: "id" },
      {
        header: "Name",
        accessorKey: "name",
        cell: ({ getValue }) => {
          const name = getValue()
          return name ? (
            name
          ) : (
            <span className="text-muted">No name provided</span>
          )
        },
      },
      {
        header: "Email",
        accessorKey: "email",
        cell: ({ getValue }) => {
          const email = getValue()
          return email ? (
            email
          ) : (
            <span className="text-muted">No email provided</span>
          )
        },
      },
      {
        header: "Mobile",
        accessorKey: "mobile",
        cell: ({ getValue }) => {
          const mobile = getValue()
          return mobile ? (
            mobile
          ) : (
            <span className="text-muted">No mobile number provided</span>
          )
        },
      },
      {
        header: "Vehicle Stocks",
        accessorKey: "vehicle_stocks_count",
        cell: ({ getValue }) => {
          const count = getValue()
          return count !== undefined ? (
            // You can add a link here to view the stocks for this importer if count > 0
            count > 0 ? (
              <Link to="#" className="text-decoration-underline">
                <Badge color="primary">
                  {count} {count === 1 ? "stock" : "stocks"}
                </Badge>
              </Link>
            ) : (
              "0 stocks"
            )
          ) : (
            <span className="text-muted">N/A</span>
          )
        },
      },
      {
        header: "Action",
        cell: ({ row }) => (
          <ul className="list-unstyled hstack gap-1 mb-0">
            <li>
              <ButtonTooltip
                type="view"
                btnId={`view-${row.original.id}`}
                onClick={() =>
                  navigate(`/importer-list/${row.original.slug}/view`)
                }
              />
            </li>
            <li>
              <ButtonTooltip
                type="edit"
                btnId={`edit-${row.original.id}`}
                onClick={() => handleImporterClick(row.original)}
              />
            </li>
            <li>
              <ButtonTooltip
                type="delete"
                btnId={`delete-${row.original.id}`}
                onClick={() => onClickDelete(row.original)}
              />
            </li>
          </ul>
        ),
      },
    ],
    [handleImporterClick, onClickDelete]
  )

  return (
    <>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteImporter}
        loading={deleteLoading}
        onCloseClick={() => setDeleteModal(false)}
      />
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs
            title="Vehicle Importer"
            breadcrumbItem="Importer List"
          />

          <Row>
            <Col lg="12">
              <Card>
                <CardBody className="border-bottom d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 card-title">Vehicle Importers</h5>
                  <div>
                    <Button color="primary" onClick={handleAddNewClick}>
                      Add New Importer
                    </Button>
                    <Button
                      color="light"
                      onClick={handleRefresh}
                      className="ms-1"
                      id="refreshTooltip"
                    >
                      <i
                        className={`mdi mdi-refresh ${
                          isRefetching ? "mdi-spin" : ""
                        }`}
                      />
                      <UncontrolledTooltip
                        placement="top"
                        target="refreshTooltip"
                      >
                        Refresh
                      </UncontrolledTooltip>
                    </Button>
                    {hasActiveFilters && (
                      <Button color="danger" onClick={resetParams}>
                        Reset Filters
                      </Button>
                    )}
                  </div>
                </CardBody>

                <CardBody>
                  <Row className="mb-3">
                    <Col sm={4}>
                      <GlobalInputSearch
                        value={params.search}
                        onChange={setSearch}
                      />
                    </Col>
                  </Row>

                  <TableContainer
                    columns={columns}
                    data={importersData}
                    isPagination
                    paginationInfo={paginationInfo}
                    onPageChange={setPage}
                    loading={isLoading || isRefetching}
                  />
                </CardBody>
              </Card>
              {detailsModal && (
                <CanView
                  access={[
                    ACCESS.STOCK.VIEW_IMPORTER,
                    ACCESS.VEHICLE_IMPORTER.VIEW_VEHICLE_IMPORTER,
                  ]}
                >
                  <ImporterDetailsModal
                    modal={detailsModal}
                    toggle={toggleDetails}
                    importer={importer}
                  />
                </CanView>
              )}
            </Col>
          </Row>
        </div>
      </div>
    </>
  )
}

export default VehicleImporterList
